// Connector platform types (ADR 0001 §1).
//
// A connector is a declarative data source: one JSON manifest in
// apps/api/connectors/*.json. The API validates it, runs it, and serves the
// result as NormalizedFeed<ConnectorRecord>. Nothing here ever carries a
// resolved secret or request headers — ConnectorSummary is browser-safe.
import type { FeatureCollection, Geometry } from "geojson";
import type { FallbackTier, SourceMeta } from "./types";

export type ConnectorKind = "http-json" | "geojson" | "csv" | "file" | "push";

export type ConnectorDomain =
  | "environment"
  | "mobility"
  | "energy"
  | "safety"
  | "facilities"
  | "utilities"
  | "people";

export const CONNECTOR_DOMAINS: readonly ConnectorDomain[] = [
  "environment", "mobility", "energy", "safety", "facilities", "utilities", "people",
];

export type ConnectorEntity = "measurement" | "event" | "asset" | "area";

/** The tier a successful run earns. Freshness degrades live → cache at serve time. */
export type ConnectorDeclaredTier = "live" | "database" | "scenario" | "reference";

export interface ConnectorMapping {
  /** JSONPath-lite to the list of items. Defaults: "$.features[*]" for geojson, "$[*]" otherwise. */
  items?: string;
  id?: string;
  label?: string;
  lat?: string;
  lng?: string;
  geometry?: string;
  observedAt?: string;
  /** Zone appended to timestamps that carry none ("Z", "+07:00"). Default "Z". */
  observedAtZone?: string;
  metrics?: Record<string, string>;
}

export interface ConnectorDisplay {
  label?: string;
  /** #RRGGBB */
  color?: string;
  icon?: string;
  /** Optional units per metric key, e.g. { "pm25": "µg/m³" } — shown in tooltips. */
  units?: Record<string, string>;
}

export interface ConnectorRequest {
  url: string;
  headers?: Record<string, string>;
  timeoutMs?: number;
}

export interface ConnectorManifest {
  id: string;
  name: string;
  owner: string;
  domain: ConnectorDomain;
  license: string;
  docs?: string;
  kind: ConnectorKind;
  /** http-json | geojson | csv only. */
  request?: ConnectorRequest;
  /** file kind only: file name inside apps/api/data/connectors/. */
  file?: string;
  schedule?: { everySeconds: number };
  /**
   * Freshness SLO. by "observedAt" (default): the newest record's observedAt
   * must be within staleAfterSeconds (an upstream can answer 200 with hours-old
   * data). by "fetch": only the last successful fetch counts — for event
   * catalogues where the newest event can legitimately be days old.
   */
  freshness?: { staleAfterSeconds: number; by?: "observedAt" | "fetch" };
  entity: ConnectorEntity;
  mapping: ConnectorMapping;
  tier: ConnectorDeclaredTier;
  display?: ConnectorDisplay;
  enabled: boolean;
}

export interface ConnectorRecord {
  id: string;
  sourceId: string;
  entity: ConnectorEntity;
  label: string | null;
  geometry: Geometry | null;
  observedAt: string | null;
  metrics: Record<string, number | string | null>;
  props: Record<string, unknown>;
}

export type ConnectorFreshness = "fresh" | "stale" | "never";

export interface ConnectorHealth {
  lastRunAt: string | null;
  lastOkAt: string | null;
  lastError: string | null;
  latencyMs: number | null;
  recordCount: number | null;
  freshness: ConnectorFreshness;
  /** Newest record observedAt (ISO), when records carry one. */
  newestObservedAt: string | null;
  /** Mapping problems from the last run (manifest id, field, path, reason). */
  warnings: string[];
}

export type ConnectorOrigin = "manifest" | "builtin";

/** One registry row — browser-safe (no URL headers, no secrets). */
export interface ConnectorSummary {
  id: string;
  name: string;
  owner: string;
  domain: ConnectorDomain;
  license: string | null;
  docs: string | null;
  kind: ConnectorKind | "builtin" | "unknown";
  entity: ConnectorEntity | null;
  origin: ConnectorOrigin;
  /** Declared tier for manifests; catalog status mapped to a tier for built-ins. */
  tier: FallbackTier;
  /** Tier the feed would be served with right now (freshness applied). */
  servedTier: FallbackTier;
  enabled: boolean;
  display: ConnectorDisplay | null;
  scheduleSeconds: number | null;
  staleAfterSeconds: number | null;
  /** Path of the data route (manifest: /api/connectors/:id; built-in: catalog apiPath). */
  apiPath: string | null;
  /** Path of the map route, when the source is mappable. */
  geojsonPath: string | null;
  /** Validation / configuration error that keeps this connector from running. */
  configError: string | null;
  health: ConnectorHealth;
}

export interface ConnectorRegistry {
  at: string;
  connectors: ConnectorSummary[];
}

/** Properties of each feature from GET /api/connectors/:id/geojson. */
export interface ConnectorFeatureProperties {
  id: string;
  sourceId: string;
  entity: ConnectorEntity;
  label: string | null;
  metrics: Record<string, number | string | null>;
  observedAt: string | null;
  /** Served tier of the whole feed — repeated per feature for tooltips. */
  tier: FallbackTier;
}

/** GET /api/connectors/:id/geojson — a FeatureCollection that also carries NormalizedFeed meta. */
export type ConnectorFeatureCollection = FeatureCollection<Geometry, ConnectorFeatureProperties> & {
  meta: SourceMeta;
};

/** Map-layer id for a connector ("connector:<id>"). */
export function connectorLayerId(id: string): `connector:${string}` {
  return `connector:${id}`;
}
